import { getTimeWithOffset } from '@dispatch/shared';
import {
  useCurrentUserAsResourceId,
  useCurrentUserResource,
} from '@dispatch/shared/hooks';
import { MultiSelectOption, TaskResponse } from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateTimeField,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { Grid } from '@mui/material';
import { isEqual } from 'date-fns';
import React, { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

export const TimingSection = ({
  control,
  errors,
  hasUpsertPermissions,
  hasAppointment,
  setValue,
  getValues,
  taskData,
  rescheduleReasons,
}: {
  errors: FieldErrors;
  control: Control<FieldValues>;
  hasUpsertPermissions: unknown;
  hasAppointment: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues> | undefined;
  taskData?: TaskResponse;
  rescheduleReasons?: MultiSelectOption[];
}) => {
  const { value: currentUserResourceId } = useCurrentUserAsResourceId();
  const { isAdjuster } = useCurrentUserResource();
  const currentAppointmentTime = taskData?.$msg.assignmentHandling?.appointment;
  let appointmentChanged = false;

  if (currentAppointmentTime) {
    appointmentChanged = !isEqual(
      new Date(
        getTimeWithOffset(
          taskData?.$msg?.claim?.lossLocation?.timeZoneId,
          currentAppointmentTime
        )
      ),
      new Date(getValues?.('assignmentHandling.appointmentStart'))
    );
  }

  const [needsReason, setNeedsReason] = React.useState(false);
  React.useEffect(() => {
    setValue?.('reasonCode', '');
    if (currentAppointmentTime && appointmentChanged) {
      // used to update validation to show that reason is required if it is shown
      setValue?.('needsReason', true);
      // used to update state to show the reason drop down
      setNeedsReason(true);
    } else {
      setValue?.('needsReason', false);
      setNeedsReason(false);
    }
  }, [appointmentChanged]);

  const handleDateCalculation = (field: string, value: string) => {
    const duration =
      (taskData?.$msg?.assignmentHandling?.durationInMinutes || 0) *
      (1 / (taskData?.$metadata?.appliedEfficiencyCoefficient || 1));

    const date = new Date(value);
    date.setMinutes(Number(date.getMinutes()) + duration);
    setValue?.(`assignmentHandling.${field}`, date);
  };

  const renderReasonCodeField = () => {
    return (
      <Grid size={6}>
        <SelectField
          id={'reasonCode'}
          name={'reasonCode'}
          label={'Reschedule Reason'}
          control={control}
          errors={errors}
          options={rescheduleReasons || []}
          required
        />
      </Grid>
    );
  };

  const canEdit = useMemo(() => {
    // complex-loss adjuster can only edit if they are assigned to the task
    // if not an adjuster then they should be able to edit if they have upsert permissions
    if (isAdjuster) {
      return (
        hasUpsertPermissions &&
        taskData?.$msg?.assignedResource?.id === currentUserResourceId
      );
    } else return hasUpsertPermissions;
  }, [
    isAdjuster,
    hasUpsertPermissions,
    taskData?.$msg?.assignedResource?.id,
    currentUserResourceId,
  ]);

  return (
    <>
      <Grid container spacing={2} alignItems="end" data-testid="loss-time-zone">
        <Grid size={3}>
          <TextField
            id="claim.timeZone"
            name="claim.timeZone"
            label="Loss Time Zone"
            required
            control={control}
            errors={errors}
            disabled
          />
        </Grid>
        <Grid size={3}>
          <TextField
            id="assignmentHandling.durationInMinutes"
            name="assignmentHandling.durationInMinutes"
            label="Expected Duration (Minutes)"
            inputProps={{ disabled: true }}
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid size={3}>
          <DateTimeField
            id="appointmentStart"
            name="assignmentHandling.appointmentStart"
            label="Appointment Start"
            control={control}
            onChange={(value) => handleDateCalculation('appointmentEnd', value)}
            errors={
              errors && errors['assignmentHandling']
                ? errors['assignmentHandling']
                : null
            }
            // complex-loss adjuster can only edit if they are assigned to the task
            // other roles that have upsert permissions that are not adjuster should still be able to edit
            readOnly={!canEdit}
          />
        </Grid>
        <Grid size={3}>
          <DateTimeField
            id="assignmentHandling.appointmentEnd"
            name="assignmentHandling.appointmentEnd"
            label="Appointment End"
            control={control}
            readOnly={true}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} marginBottom={2} alignItems="center">
        <Grid size={6}>
          <CheckBoxField
            control={control}
            id="assignmentHandling.travel"
            name="assignmentHandling.travel"
            label="Travel Required"
            disabled
          />
        </Grid>
        {!hasAppointment ? (
          <>
            {/*Show if there is no reason. This is needed for an offset. Offets are supported in mui Grid v2, so when we update we can remove this */}
            <Grid size={3}>
              <DateTimeField
                id="assignmentHandling.mustStartAfterTime"
                name="assignmentHandling.mustStartAfterTime"
                label="Projected Start Time"
                control={control}
                errors={errors}
                readOnly={true}
              />
            </Grid>
            <Grid size={3}>
              <DateTimeField
                id="assignmentHandling.mustEndBeforeTime"
                name="assignmentHandling.mustEndBeforeTime"
                label="Projected End Time"
                control={control}
                errors={errors}
                readOnly={true}
              />
            </Grid>
            <Grid size={6}></Grid>
            {renderReasonCodeField()}
          </>
        ) : (
          needsReason === true && renderReasonCodeField()
        )}
      </Grid>
    </>
  );
};
